import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { useNotify } from "../../components/notifications";
import { useSaveFlow, useConfirm } from "../../components/confirm";
import { AppDialog } from "../../components/AppDialog";
import { useHotkeys, formatCombo } from "../../components/hotkeys";
import "./SettingsDetailPage.css"; // reuse the Settings-detail `.sd-*` chrome

/**
 * Settings → Master Data Initialization → Initialization Templates — bespoke full-page
 * detail (legacy MasterDataInitialization_DetailView). Header (MDI Version · MDI Name* ·
 * Excel Attachment with Browse / Load / Reset) + a "Templates" child grid. "Import Template
 * Sheets" reads the attached workbook's SHEET NAMES into the grid (one row per sheet); each
 * row then carries an editable Query / BufferTable / BOInvolved / Comment. Save persists the
 * header + the whole child set in one transaction through the shared useSaveFlow (§18.6).
 * The master-data import EXECUTION engine (legacy MDIProcessController) is a deferred slice.
 */

interface TplRow {
  oid: string | null;
  templateId: string | null;
  templateName: string;
  importIndex: number;
  query: string;
  bufferTable: string;
  boInvolved: string;
  comment: string;
}

interface Props {
  id: string | null;
  pageId?: string;
  title?: string;
  group?: string;
  onHome?: () => void;
  /** Called after a successful save; carries the new/updated record's Oid so a
   *  caller (e.g. the Initialization Log "+ Add MDI Name" popup) can select it. */
  onSaved: (newId?: string) => void;
  onClose: () => void;
  /** Render as an embedded dialog body (no full-page chrome / breadcrumb), with its
   *  own OK / Cancel footer — used inside the Initialization Log "Add MDI Name" popup
   *  (owner request 2026-10-02). Default false = the full-page detail. */
  embedded?: boolean;
}

const API = "/api/v1/mdi-templates";

function apiErr(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
    ?? (e instanceof Error ? e.message : fallback);
}

export function MdiTemplatesDetail({ id, title, group, onHome, onSaved, onClose, embedded = false }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();

  // Internal record id (seeded from the `id` prop) so Save keeps you on the saved
  // record and Save and New clears to a blank one, without the parent re-mounting us
  // (owner request 2026-10-16 — full Save trio like the shared detail page).
  const [recordId, setRecordId] = useState<string | null>(id ?? null);
  const [loading, setLoading] = useState<boolean>(id != null);
  const [mdiName, setMdiName] = useState("");
  const [mdiVersion, setMdiVersion] = useState("");
  const [attachmentOid, setAttachmentOid] = useState<string | null>(null);
  const [attachmentFileName, setAttachmentFileName] = useState<string>("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState<TplRow[]>([]);
  // QueryEditor popup (legacy per-row "QueryEditor" link → Query textarea dialog).
  const [queryEditIdx, setQueryEditIdx] = useState<number | null>(null);
  const [queryDraft, setQueryDraft] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // Keep the internal record id in step if the parent ever swaps the `id` prop.
  useEffect(() => { setRecordId(id ?? null); }, [id]);

  // ── Load (edit) ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (recordId == null) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`${API}/${recordId}`);
        const d = (res.data as { data: Record<string, unknown> }).data;
        if (cancelled) return;
        setMdiName(String(d.mdiName ?? ""));
        setMdiVersion(String(d.mdiVersion ?? ""));
        setAttachmentOid(d.attachmentOid ? String(d.attachmentOid) : null);
        setAttachmentFileName(String(d.attachmentFileName ?? ""));
        const tpl = Array.isArray(d.templates) ? (d.templates as Record<string, unknown>[]) : [];
        setRows(tpl.map((r) => ({
          oid: r.oid ? String(r.oid) : null,
          templateId: r.templateId ? String(r.templateId) : null,
          templateName: String(r.templateName ?? ""),
          importIndex: Number(r.importIndex ?? 0),
          query: String(r.query ?? ""),
          bufferTable: String(r.bufferTable ?? ""),
          boInvolved: String(r.boInvolved ?? ""),
          comment: String(r.comment ?? ""),
        })));
      } catch (e) {
        if (!cancelled) notify.error(`Could not load the template: ${apiErr(e, "API error.")}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordId, notify]);

  const recordName = mdiName.trim() || (recordId == null ? "New Initialization Template" : "Initialization Template");

  // ── Attachment: Browse (pick) → Load (upload) → Reset (clear) ────────────────
  const onLoad = async () => {
    if (!pendingFile) { notify.warning("Choose a file with Browse first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      const res = await api.post(`${API}/file/upload`, fd);
      const d = (res.data as { data: { fileDataOid: string; fileName: string } }).data;
      setAttachmentOid(d.fileDataOid);
      setAttachmentFileName(d.fileName);
      notify.success("File loaded.");
    } catch (e) {
      notify.error(`Upload failed: ${apiErr(e, "API error.")}`);
    } finally {
      setUploading(false);
    }
  };
  const onReset = () => {
    setPendingFile(null);
    setAttachmentOid(null);
    setAttachmentFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── Import Template Sheets — parse the attached workbook's sheet names ────────
  const onImportSheets = async () => {
    if (!attachmentOid) { notify.warning("Load the Excel file first, then import."); return; }
    const ok = await confirm.confirm({
      title: "Import Template Sheets",
      // Message removed + brand-orange accent (owner request 2026-10-16).
      message: "",
      accent: "brand",
    });
    if (!ok) return;
    setImporting(true);
    try {
      const res = await api.post(`${API}/import-sheets`, { attachmentOid });
      const sheets = (res.data as { data: Record<string, unknown>[] }).data ?? [];
      setRows(sheets.map((r, i) => ({
        oid: null,
        templateId: r.templateId ? String(r.templateId) : null,
        templateName: String(r.templateName ?? ""),
        importIndex: Number(r.importIndex ?? i),
        query: "", bufferTable: "", boInvolved: "", comment: "",
      })));
      notify.success(`Imported ${sheets.length} template sheet(s).`);
    } catch (e) {
      notify.error(apiErr(e, "Could not import the template sheets."));
    } finally {
      setImporting(false);
    }
  };

  const patchRow = (idx: number, patch: Partial<TplRow>) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const clearForNew = () => {
    setRecordId(null);
    setMdiName(""); setMdiVersion("");
    setAttachmentOid(null); setAttachmentFileName(""); setPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
    setRows([]);
  };

  // after: 'stay' keeps you on the saved record, 'close' returns to the list,
  // 'new' clears to a blank record (Save trio, BO UI Standard §4).
  const doSave = async (after: "stay" | "close" | "new") => {
    if (!mdiName.trim()) { notify.error("MDI Name must not be empty."); return; }
    let savedId: string | undefined = recordId ?? undefined;
    await runSave({
      formName: "Initialization Templates",
      auditAction: recordId == null ? "create" : "edit",
      save: async () => {
        const payload = {
          mdiName: mdiName.trim(),
          mdiVersion: mdiVersion.trim() || null,
          attachmentOid,
          templates: rows
            .filter((r) => r.templateName.trim())
            .map((r) => ({
              oid: r.oid, templateId: r.templateId, templateName: r.templateName.trim(),
              importIndex: r.importIndex, query: r.query, bufferTable: r.bufferTable.trim(),
              boInvolved: r.boInvolved, comment: r.comment.trim(),
            })),
        };
        if (recordId == null) {
          const res = await api.post(API, payload);
          savedId = (res.data as { data?: { id?: string } }).data?.id ?? undefined;
        } else {
          await api.put(`${API}/${recordId}`, payload);
        }
      },
      onSaved: () => {
        if (after === "close") { onSaved(savedId); return; }
        if (after === "new") { clearForNew(); return; }
        // 'stay' — remain on the saved record (a create reloads via recordId).
        setRecordId(savedId ?? null);
      },
    });
  };

  // Keyboard shortcuts (standing rule 14) — mirror the Save / Cancel buttons.
  useHotkeys(useMemo(() => [
    { combo: "mod+s", label: "Save", group: "Initialization Templates", order: 1, run: () => void doSave(embedded ? "close" : "stay") },
    { combo: "esc", label: "Cancel", group: "Initialization Templates", order: 2, allowInInput: false, run: () => onClose() },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [mdiName, mdiVersion, attachmentOid, rows, recordId, embedded]));

  // EMBEDDED popup footer — OK / Cancel (the AppDialog is the container).
  const embeddedActions = (
    <>
      <button className="ca-btn" onClick={() => void doSave("close")} title={`Save (${formatCombo("mod+s")})`}>
        OK
      </button>
      <button className="ca-btn" onClick={onClose} title={`Cancel (${formatCombo("esc")})`}>
        Cancel
      </button>
    </>
  );
  // FULL-PAGE Save trio (BO UI Standard §4 / rule 12) — Save · Save and Close ·
  // Save and New · | · Cancel, rendered at TOP and BOTTOM, matching SettingsDetailPage.
  const trioBar = (bottom?: boolean) => (
    <div className={`sd-actionbar${bottom ? " bottom" : ""}`}>
      <button className="sd-abtn primary" onClick={() => void doSave("stay")} title={`Save (${formatCombo("mod+s")})`}>
        <span className="tb-i">💾</span> Save
      </button>
      <button className="sd-abtn" onClick={() => void doSave("close")}>
        <span className="tb-i">💾</span> Save and Close
      </button>
      <button className="sd-abtn" onClick={() => void doSave("new")}>
        <span className="tb-i">📄</span> Save and New
      </button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button className="sd-abtn cancel" onClick={onClose} title={`Cancel (${formatCombo("esc")})`}>
        <span className="tb-i">↩︎</span> Cancel
      </button>
    </div>
  );

  return (
    <div className={embedded ? "mdi-embedded" : "bl-page mlv-host"}>
      <div className="bl-grid-area">
        <div className="sd-wrap">
          {/* Full-page header: title + breadcrumb only. The Save trio renders as its OWN
              bar below (top) and after the body (bottom), matching SettingsDetailPage
              (owner request 2026-10-16). Hidden when embedded in the "Add MDI Name" popup. */}
          {!embedded && (
            <>
              <div className="sd-head">
                <div style={{ minWidth: 0 }}>
                  {/* "{BO} – {record}" title + breadcrumb, matching the shared
                      SettingsDetailPage header (owner request 2026-10-16, image 733). */}
                  <h2>{(title || "Initialization Templates")} – {recordName}</h2>
                  <div className="sd-bc">
                    {onHome && (<><a onClick={onHome}>Home</a><i className="ti ti-chevron-right" /></>)}
                    {group && (<><span>{group}</span><i className="ti ti-chevron-right" /></>)}
                    {title && (<><a onClick={onClose}>{title}</a><i className="ti ti-chevron-right" /></>)}
                    <span>{recordName}</span>
                  </div>
                </div>
                {/* Prev/next record arrows — visual parity with the standard header.
                    This bespoke page does not wire record navigation, so they are
                    display-only (disabled), as the standard shows on a New record. */}
                <div className="sd-navarrows">
                  <button className="sd-na" title="Previous record" disabled>‹</button>
                  <button className="sd-na next" title="Next record" disabled>›</button>
                </div>
              </div>
              {trioBar()}
            </>
          )}

          <div className="sd-body">
            {loading ? (
              <div className="sd-loading">Loading…</div>
            ) : (
              <>
                <h3 className="sd-section-h">General Information</h3>
                <div className="mdi-form">
                  <label className="mdi-row">
                    <span className="mdi-lbl">MDI Version:</span>
                    <input className="mdi-in" value={mdiVersion} maxLength={100} onChange={(e) => setMdiVersion(e.target.value)} />
                  </label>
                  <label className="mdi-row">
                    <span className="mdi-lbl mdi-req">MDI Name:<span className="mdi-star">*</span></span>
                    <input className="mdi-in" value={mdiName} maxLength={100} required onChange={(e) => setMdiName(e.target.value)} />
                  </label>
                  <div className="mdi-row">
                    <span className="mdi-lbl">Attachment:</span>
                    <div className="mdi-att">
                      <input ref={fileRef} type="file" accept=".xlsx" className="mdi-file"
                        onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)} />
                      <button className="ca-btn" onClick={() => void onLoad()} disabled={uploading || !pendingFile}>
                        <i className="ti ti-upload" /> {uploading ? "Loading…" : "Load"}
                      </button>
                      <button className="ca-btn" onClick={onReset} disabled={uploading}>
                        <i className="ti ti-refresh" /> Reset
                      </button>
                      {/* Import Template Sheets moved next to Reset (owner request 2026-10-16). */}
                      <button className="ca-btn" onClick={() => void onImportSheets()} disabled={importing || !attachmentOid}
                        title="Read sheet names from the loaded Excel file">
                        <i className="ti ti-file-import" /> {importing ? "Importing…" : "Import Template Sheets"}
                      </button>
                      {attachmentOid && attachmentFileName && (
                        <a className="mdi-attlink" href={`${api.defaults.baseURL ?? ""}${API}/file/${attachmentOid}/download`} target="_blank" rel="noreferrer">
                          <i className="ti ti-paperclip" /> {attachmentFileName}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mdi-tplhead">
                  <h3 className="sd-section-h">Templates</h3>
                </div>
                <div className="mdi-gridwrap">
                  <table className="mdi-grid">
                    <thead>
                      <tr>
                        <th style={{ width: 200 }}>TemplateName</th>
                        <th style={{ width: 90 }}>ImportIndex</th>
                        <th style={{ width: 240 }}>QueryEditor</th>
                        <th style={{ width: 140 }}>BufferTable</th>
                        <th style={{ width: 240 }}>BOInvolved</th>
                        <th style={{ width: 180 }}>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={6} className="mdi-empty">No data to display</td></tr>
                      ) : rows.map((r, i) => (
                        <tr key={i}>
                          <td><input className="mdi-cell" value={r.templateName} onChange={(e) => patchRow(i, { templateName: e.target.value })} /></td>
                          <td><input className="mdi-cell" type="number" value={r.importIndex} onChange={(e) => patchRow(i, { importIndex: Number(e.target.value) || 0 })} /></td>
                          <td>
                            {/* Legacy per-row "QueryEditor" hyperlink → Query textarea popup
                                (owner report 2026-10-22). Replaces the inline cell. */}
                            <button type="button" className="mdi-qe-link"
                              title={r.query || "Edit the import query for this sheet"}
                              onClick={() => { setQueryDraft(r.query); setQueryEditIdx(i); }}>
                              QueryEditor
                            </button>
                          </td>
                          <td><input className="mdi-cell" value={r.bufferTable} onChange={(e) => patchRow(i, { bufferTable: e.target.value })} /></td>
                          <td><input className="mdi-cell" value={r.boInvolved} onChange={(e) => patchRow(i, { boInvolved: e.target.value })} /></td>
                          <td><input className="mdi-cell" value={r.comment} onChange={(e) => patchRow(i, { comment: e.target.value })} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          {!embedded && trioBar(true)}
          {embedded && <div className="mdi-embed-foot">{embeddedActions}</div>}
        </div>
      </div>
      {queryEditIdx !== null && (
        <AppDialog
          open
          title="Initialization Template"
          icon="ti-table"
          width={760}
          submitOnEnter={false}
          onClose={() => setQueryEditIdx(null)}
          footer={<>
            <button
              className="ca-btn ca-btn-solid"
              onClick={() => { patchRow(queryEditIdx, { query: queryDraft }); setQueryEditIdx(null); }}
            >
              OK
            </button>
            <button className="ca-btn" onClick={() => setQueryEditIdx(null)}>Cancel</button>
          </>}
        >
          <div className="mdi-qe">
            <label className="mdi-qe-lbl">Query:</label>
            <textarea
              className="mdi-qe-area"
              value={queryDraft}
              spellCheck={false}
              onChange={(e) => setQueryDraft(e.target.value)}
            />
          </div>
        </AppDialog>
      )}
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  /* Embedded (inside the Initialization Log "Add MDI Name" popup) — plain block, no
     full-page chrome; the AppDialog is the container and provides the scroll. */
  .mdi-embedded { display: block; }
  .mdi-embedded .bl-grid-area, .mdi-embedded .sd-wrap, .mdi-embedded .sd-body { display: block; height: auto; padding: 0; overflow: visible; }
  /* No card border/radius/shadow/margin when embedded in a dialog — the AppDialog is
     the frame, so .sd-wrap's card border would double up (owner report 2026-10-22). */
  .mdi-embedded .sd-wrap { border: 0; border-radius: 0; box-shadow: none; margin-top: 0; }
  /* +10px below + 10px left on the OK/Cancel footer (owner request 2026-10-16). */
  .mdi-embed-foot { display: flex; justify-content: flex-end; gap: 8px; padding-top: 12px; margin: 8px 0 10px 10px; border-top: 1px solid var(--ai-border, #eef0f4); }
  .mdi-form { max-width: 720px; display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; }
  .mdi-row { display: flex; align-items: center; gap: 10px; }
  .mdi-lbl { width: 120px; flex: 0 0 120px; color: var(--ai-ink, #1f2430); font-size: 13px; }
  /* Required label = the same ink as every other label, with only the star red (the shared .sd-label .req look). */
  .mdi-req { color: var(--ai-ink, #1f2430); }
  .mdi-star { color: var(--ai-red, #c0392b); margin-left: 2px; }
  /* Match the shared .sd-input size (owner request 2026-10-16, image 735 — "controls
     looking very big"): 26px compact box + a capped width, not full-stretch. */
  .mdi-in { flex: 1 1 auto; max-width: 380px; min-height: var(--ai-control-h, 26px); padding: var(--ai-control-pad-y, 4px) 10px; border: 1px solid var(--ai-border, #d4d8e0); border-radius: 0; font-size: 13px; box-sizing: border-box; }
  /* One focus look for every input here: square, the orange edge, no glow. */
  .mdi-in:focus, .mdi-qe-area:focus { border-color: var(--brand-accent, var(--ai-orange)); outline: none; box-shadow: none; }
  .mdi-att { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .mdi-file { font-size: 12px; }
  .mdi-attlink { font-size: 12px; color: var(--ai-orange, #e8670a); text-decoration: none; display: inline-flex; align-items: center; gap: 4px; }
  .mdi-tplhead { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 6px; }
  .mdi-gridwrap { border: 1px solid var(--ai-border, #e3e6ec); border-radius: 6px; overflow: auto; margin-top: 8px; background: var(--ai-card, #fff); }
  .mdi-grid { width: 100%; border-collapse: collapse; font-size: 13px; }
  .mdi-grid th { text-align: left; padding: 8px 8px; background: #fafbfc; color: var(--ai-ink, #1f2430); font-weight: 600; border-bottom: 1px solid var(--ai-border, #e3e6ec); white-space: nowrap; }
  .mdi-grid td { padding: 4px 6px; border-bottom: 1px solid var(--ai-border, #eef0f4); vertical-align: middle; }
  .mdi-empty { text-align: center; color: var(--ai-muted, #6b7280); padding: 20px; }
  .mdi-cell { width: 100%; height: 26px; padding: 0 6px; border: 1px solid transparent; border-radius: 0; font-size: 13px; background: transparent; }
  .mdi-cell:focus { border-color: var(--brand-accent, var(--ai-orange)); background: #fff; outline: none; box-shadow: none; }
  /* QueryEditor column: a per-row hyperlink opening the Query popup (legacy parity). */
  .mdi-qe-link { border: 0; background: transparent; color: var(--ai-orange, #e8670a); cursor: pointer; padding: 2px 4px; font-size: 13px; }
  .mdi-qe-link:hover { text-decoration: underline; }
  /* Query popup body: "Query:" label beside a tall textarea (legacy layout). */
  .mdi-qe { display: flex; gap: 10px; align-items: flex-start; }
  .mdi-qe-lbl { flex: 0 0 auto; color: var(--ai-ink, #1f2430); font-size: 13px; padding-top: 6px; }
  .mdi-qe-area { flex: 1 1 auto; min-height: 280px; resize: vertical; padding: 8px; border: 1px solid var(--ai-border, #d4d8e0); border-radius: 0; font-family: "JetBrains Mono", Consolas, monospace; font-size: 12.75px; box-sizing: border-box; }
`;
